import React, {
    Children,
    cloneElement,
    MouseEvent,
    MutableRefObject,
    ReactElement,
    ReactNode,
    Ref,
    useCallback,
    useRef,
    useState,
} from 'react';

import Menu from '@mui/material/Menu';

const menuPaperSx = (minWidth: number) => {
    return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minWidth,
        borderRadius: '8px',
        borderWidth: '2px',
        borderColor: '#E5E5E5',
        borderStyle: 'solid',
        padding: 0,
    };
};

interface CustomDropdownMenuItemProps {
    keepOpen?: boolean;
    menu?: ReactElement<CustomDropdownMenuItemProps>[];
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    forceClose?: () => void;
    children?: ReactNode;
}

interface CustomDropdownProps {
    trigger: ReactElement;
    menu: ReactElement<CustomDropdownMenuItemProps>[];
    keepOpen?: boolean;
    isOpen?: HTMLElement | null;
    onOpen?: (anchor: HTMLElement | null) => void;
    minWidth?: number;
    ref?: Ref<HTMLElement>;
}

export function CustomDropdown({
    trigger,
    menu,
    keepOpen: globalKeepOpen = false,
    isOpen: controlledAnchor,
    onOpen,
    minWidth = 0,
    ref,
}: CustomDropdownProps) {
    const [internalAnchor, setInternalAnchor] = useState<HTMLElement | null>(null);
    const localRef = useRef<HTMLElement | null>(null);

    const setAnchorRef = useCallback(
        (node: HTMLElement | null) => {
            localRef.current = node;

            if (typeof ref === 'function') {
                ref(node);
            } else if (ref && 'current' in ref) {
                (ref as MutableRefObject<HTMLElement | null>).current = node;
            }
        },
        [ref]
    );

    const anchorElement = controlledAnchor ?? internalAnchor;
    const isMenuOpen = Boolean(anchorElement);

    const openMenu = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (!menu.length) return;

        const anchor = event.currentTarget;
        if (onOpen) onOpen(anchor);
        else setInternalAnchor(anchor);
    };

    const forceClose = useCallback(() => {
        if (onOpen) onOpen(null);
        else setInternalAnchor(null);
    }, [onOpen]);

    const closeMenu = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (localRef.current?.contains(event.target as Node)) return;
        forceClose();
    };

    const renderMenuItem = (
        item: ReactElement<CustomDropdownMenuItemProps>,
        index: number
    ): ReactElement => {
        const {
            keepOpen: itemKeepOpen = false,
            menu: nestedMenu,
            onClick,
            children,
            ...restProps
        } = item.props;

        const shouldStayOpen = globalKeepOpen || itemKeepOpen;

        const handleItemClick = (evt: MouseEvent<HTMLElement>) => {
            evt.stopPropagation();
            if (!shouldStayOpen) closeMenu(evt);
            onClick?.(evt);
        };

        return cloneElement(item, {
            key: index,
            ...restProps,
            onClick: handleItemClick,
            forceClose,
            ...(nestedMenu && { parentMenuOpen: isMenuOpen }),
            children: nestedMenu ? Children.map(nestedMenu, renderMenuItem) : children,
        });
    };

    return (
        <>
            {cloneElement(trigger, {
                onClick: isMenuOpen ? forceClose : openMenu,
                ref: setAnchorRef,
            })}

            <Menu
                anchorEl={anchorElement}
                open={isMenuOpen}
                onClose={closeMenu}
                slotProps={{
                    paper: {
                        sx: {
                            ...menuPaperSx(minWidth),
                        },
                    },
                }}
            >
                {Children.map(menu, renderMenuItem)}
            </Menu>
        </>
    );
}

export default CustomDropdown;
